import { useState, useMemo, useRef, useEffect } from 'react'
import { debounce } from 'lodash'

// evertel
import { useService } from '@evertel/di'
import { PasswordController } from '../controllers/PasswordController'

type ResultSeverity = 'bad' | 'good' | 'great'

type Result = {
    message: string
    severity: ResultSeverity
}

interface UserData {
    firstName?: string
    lastName?: string
    email?: string
}

export const usePasswordValidation = (
    userData?: UserData,
    defaultSuggestions?: string[]
) => {
    // Memoize userData to prevent unnecessary debounce recreation when consumer
    // passes a new object reference with the same values (e.g., passing a larger
    // state object that includes other frequently-changing fields like password)
    const memoizedUserData = useMemo(() => ({
        firstName: userData?.firstName,
        lastName: userData?.lastName,
        email: userData?.email
    }), [userData?.firstName, userData?.lastName, userData?.email])

    // Memoize fallbackSuggestions to prevent recreating on every render
    const fallbackSuggestions = useMemo(() => defaultSuggestions || [
        'Password must be a minimum of 20 characters',
        'Password must not be identical to previous'
    ], [defaultSuggestions])

    const [password, setPassword] = useState('')
    const [score, setScore] = useState(0)
    const [result, setResult] = useState<Result>({ message: '', severity: 'bad' })
    const [warning, setWarning] = useState('')
    const [suggestions, setSuggestions] = useState(fallbackSuggestions)
    const [isValid, setIsValid] = useState(false)
    const passwordController = useService(PasswordController)

    // Use ref to read current result.message without recreating debounce
    const resultMessageRef = useRef(result.message)
    useEffect(() => {
        resultMessageRef.current = result.message
    }, [result.message])

    // Track request ID to handle race conditions (ignore stale responses)
    const requestIdRef = useRef(0)

    const resetState = () => {
        setWarning('')
        setScore(0)
        setSuggestions(fallbackSuggestions)
        setResult({ message: '', severity: 'bad' })
        setIsValid(false)
    }

    const debouncedValidate = useMemo(
        // eslint bug: https://github.com/facebook/react/issues/34775
        // eslint-disable-next-line react-hooks/refs
        () => debounce(async (newPassword: string) => {
            if (newPassword.length === 0) {
                resetState()
                return
            }

            /**
             * The first time it runs result.message will be empty. Once it receives
             * the "Too short" message, it won't run again until the password is over 20 characters.
             * We read from resultMessageRef to get the current value without stale closure.
             */
            if (resultMessageRef.current !== 'Too Short' || newPassword.length >= 20) {
                // Increment request ID and capture it for this request
                const thisRequestId = ++requestIdRef.current
                let response

                try {
                    response = await passwordController.validate({
                        password: newPassword,
                        firstName: memoizedUserData.firstName,
                        lastName: memoizedUserData.lastName,
                        email: memoizedUserData.email
                    })
                } catch (e) {
                    response = {
                        score: null,
                        warning: 'Error validating password.',
                        suggestions: fallbackSuggestions,
                        result: { message: 'Unknown', severity: 'bad' },
                        isValid: false
                    }
                }

                // Only update state if this is still the latest request
                if (thisRequestId !== requestIdRef.current) {
                    return // Stale response, ignore it
                }

                setScore(response.score)
                setResult(response.result)
                setWarning(response.warning)
                setSuggestions(response.suggestions)
                setIsValid(response.isValid)
            }
        }, 300),
        [memoizedUserData, fallbackSuggestions]
    )

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedValidate.cancel()
        }
    }, [debouncedValidate])

    const validatePassword = (newPassword: string) => {
        setPassword(newPassword)
        debouncedValidate(newPassword)
    }

    return {
        password,
        score,
        result,
        warning,
        suggestions,
        isValid,
        validatePassword
    }
}
